export type TeamScoringEntry = {
  joined: boolean;
  team: string;
  teamColor: string;
  finishPosition?: number | null;
};

export type RallyTeamResult = {
  name: string;
  color: string;
  averagePosition: number;
  effectivePositions: number[];
  rank: number;
};

export const formatRallyTeamAverage = (averagePosition: number) =>
  averagePosition.toFixed(2);

type UnrankedTeamResult = Omit<RallyTeamResult, 'rank'> & {
  totalPosition: number;
  participantCount: number;
};

const compareTeamResults = (a: UnrankedTeamResult, b: UnrankedTeamResult) => {
  // Compare the averages as fractions so exact ties stay exact.
  const averageDifference =
    a.totalPosition * b.participantCount - b.totalPosition * a.participantCount;
  if (averageDifference !== 0) return averageDifference;

  const comparablePositions = Math.min(
    a.effectivePositions.length,
    b.effectivePositions.length
  );
  for (let index = 0; index < comparablePositions; index += 1) {
    const positionDifference = a.effectivePositions[index] - b.effectivePositions[index];
    if (positionDifference !== 0) return positionDifference;
  }

  return 0;
};

export const calculateRallyTeamResults = (
  entries: TeamScoringEntry[]
): RallyTeamResult[] => {
  const participants = entries.filter((entry) => entry.joined);
  const dnfPosition = participants.length + 1;
  const teams = new Map<
    string,
    { name: string; color: string; effectivePositions: number[] }
  >();

  participants.forEach((entry) => {
    const team = teams.get(entry.team) || {
      name: entry.team,
      color: entry.teamColor,
      effectivePositions: [],
    };
    team.effectivePositions.push(entry.finishPosition ?? dnfPosition);
    teams.set(entry.team, team);
  });

  const sorted: UnrankedTeamResult[] = Array.from(teams.values())
    .map((team) => {
      const effectivePositions = [...team.effectivePositions].sort((a, b) => a - b);
      const totalPosition = effectivePositions.reduce(
        (total, position) => total + position,
        0
      );
      return {
        ...team,
        effectivePositions,
        totalPosition,
        participantCount: effectivePositions.length,
        averagePosition: totalPosition / effectivePositions.length,
      };
    })
    .sort(compareTeamResults);

  const ranked: RallyTeamResult[] = [];
  sorted.forEach((team, index) => {
    const tiedWithPrevious =
      index > 0 && compareTeamResults(sorted[index - 1], team) === 0;
    ranked.push({
      name: team.name,
      color: team.color,
      averagePosition: team.averagePosition,
      effectivePositions: team.effectivePositions,
      rank: tiedWithPrevious ? ranked[index - 1].rank : index + 1,
    });
  });
  return ranked;
};
